/**
 * Operations orders, missions 13-20: demolition (10 §2). Each states its special rule as an order, names
 * the equipment the job cannot be done without, and says how it ends. tests/unit/uiBriefing.test.ts holds a
 * phrase from every rule here and must be kept in step.
 */

import type { ArchetypeOrder } from './orders';
import { ALERTED, fixed, FULL_ALARM, NO_NOISE_RULE, ON_THE_CLOCK, QUIET_PISTOL, rules } from './orderKit';

type DemolitionId =
  | 'blow-bridge' | 'supply-train' | 'artillery-battery' | 'relay-mast'
  | 'missile-convoy' | 'fuel-depot' | 'coastal-guns' | 'ammo-barges';

export const DEMOLITION_ORDERS: Readonly<Record<DemolitionId, ArchetypeOrder>> = {
  'blow-bridge': {
    situation: 'A ROAD BRIDGE OVER THE RIVER, CARRIED ON TWO OR THREE CONCRETE PIERS. SENTRIES ON BOTH BANKS.'
      + ' AN ENEMY CONVOY IS DUE ACROSS IT.',
    mission: fixed('PLANT A TIME BOMB ON EVERY PIER AND DROP THE SPAN.'),
    execution: [
      'THE PIERS STAND IN THE RIVER. GET TO EACH ONE AND SET THE CHARGE AGAINST IT.',
      'EVERY PIER COUNTS. ONE PIER LEFT STANDING AND THE BRIDGE STANDS.',
    ],
    special: rules(
      'THE PIERS ARE ARMOURED: ONLY A TIME BOMB WORKS. THE LAW AND THE GRENADE DO NOTHING TO THEM.',
      `THE CONVOY CROSSES AT A STATED TIME. ${ON_THE_CLOCK} IT CANNOT LOSE YOU THE MISSION: IT PUTS MORE MEN ON`
        + ' THE ROAD. COMMAND WOULD LIKE THE SPAN TO GO DOWN UNDER IT.',
      NO_NOISE_RULE,
    ),
    equipment: [
      'ONE TIME BOMB FOR EACH PIER: AT LEAST TWO, THREE TO BE SAFE. YOU CARRY ONE. PACK THE REST, AND A SPARE, IN'
        + ' THE PODS. NO BOMBS, NO MISSION.',
    ],
  },
  'supply-train': {
    situation: 'A RAIL YARD. A LOCOMOTIVE AND ITS FUEL WAGONS ARE MAKING UP A SUPPLY TRAIN, AND IT LEAVES ON A'
      + ' TIMETABLE.',
    mission: fixed('DESTROY THE LOCOMOTIVE AND THE FUEL WAGONS BEFORE THE TRAIN LEAVES.'),
    execution: [
      'THE LOCOMOTIVE IS HARD: A LAW OR A TIME BOMB. GRENADES AND THE CARBINE WILL NOT STOP IT.',
      'THE FUEL WAGONS ARE SOFT: A GRENADE IS ENOUGH, AND ONE GOING UP CAN TAKE THE NEXT. BE WELL CLEAR.',
    ],
    special: rules(
      `THE TRAIN LEAVES AT A STATED TIME. ${ON_THE_CLOCK} WHEN IT REACHES ZERO WHATEVER IS STILL STANDING DRIVES`
        + ' OUT OF THE YARD, AND IF THE JOB IS NOT DONE THE MISSION HAS FAILED.',
      `${FULL_ALARM} HALVES THE TIME LEFT. THEY WILL NOT WAIT FOR YOU.`,
      'THERE IS NO KNIFE RULE, BUT NOISE COSTS YOU TIME: STAY QUIET UNTIL YOU ARE AMONG THE WAGONS.',
    ),
    equipment: ['A LAW OR A TIME BOMB FOR THE LOCOMOTIVE. GRENADES FOR THE WAGONS.'],
    time: "THE TRAIN'S DEPARTURE IS THE CLOCK THAT MATTERS, AND IT IS SHORTER THAN THE MISSION CLOCK.",
  },
  'artillery-battery': {
    situation: 'A FIELD BATTERY: THREE OR FOUR GUNS IN SANDBAGGED PITS, EACH WITH ITS CREW, AMMUNITION STACKED'
      + ' BESIDE IT.',
    mission: fixed('SPIKE THE GUNS. THREE OF THEM IS SUCCESS; EVERY GUN IS THE BONUS.'),
    execution: [
      'A GUN IS HARD: ONE LAW OR ONE TIME BOMB EACH. GRENADES AND THE CARBINE WILL NOT SPIKE IT.',
      'THE AMMUNITION IN THE PITS COOKS OFF WHEN A GUN GOES. BE OUT OF THE PIT AND WELL CLEAR.',
    ],
    special: rules(
      'THE GUNS ARE MANNED AND THE CREWS FIGHT. CLEAR A PIT BEFORE YOU WORK IN IT.',
      NO_NOISE_RULE,
    ),
    equipment: [
      'AT LEAST THREE LAWS OR TIME BOMBS, ONE FOR EACH GUN. STANDARD ISSUE IS ONE OF EACH: PACK MORE IN THE PODS.',
    ],
  },
  'relay-mast': {
    situation: 'A RADIO RELAY MAST ON A HILLTOP WITH A GENERATOR HUT AT ITS FOOT. THE SEARCHLIGHTS ON THE'
      + ' APPROACHES RUN OFF THAT GENERATOR.',
    mission: fixed('BRING DOWN THE MAST AND DESTROY THE GENERATOR HUT. BOTH.'),
    execution: [
      'THE GENERATOR HUT IS SOFT: A GRENADE, A LAW OR A BOMB.',
      'KILL THE GENERATOR FIRST AND EVERY SEARCHLIGHT ON THE MAP GOES DARK. THEN DEAL WITH THE MAST.',
    ],
    special: rules(
      'THE MAST IS ANCHORED IN CONCRETE: ONLY A TIME BOMB BRINGS IT DOWN.',
      NO_NOISE_RULE,
    ),
    equipment: ['A TIME BOMB FOR THE MAST. NO BOMB, NO MISSION: CARRY ONE AND PACK A SPARE IN A POD.'],
  },
  'missile-convoy': {
    situation: 'MOBILE MISSILE LAUNCHERS PARKED IN A LAAGER, CREWS RESTING, ENGINES COLD. THEY CAN BE ON THE ROAD'
      + ' INSIDE A MINUTE.',
    mission: fixed('DESTROY EVERY LAUNCHER IN THE LAAGER.'),
    execution: [
      'A LAUNCHER IS HARD: A LAW OR A TIME BOMB. IT GOES UP WITH ITS MISSILES, AND A CHARGE BETWEEN TWO MAY TAKE'
        + ' BOTH. BE CLEAR.',
    ],
    special: rules(
      `KNIFE ONLY UNTIL YOU ARE INSIDE THE LAAGER. ${ALERTED} BEFORE YOU ARE INSIDE THE LAAGER, THE LAUNCHERS WILL`
        + ' DRIVE OFF: 45 SECONDS OF ENGINES, THEN AN EMPTY LAAGER AND A FAILED MISSION.',
      'ONCE YOU ARE INSIDE THE LAAGER THEY ARE GOING NOWHERE. MAKE ALL THE NOISE YOU LIKE.',
    ),
    equipment: ['A LAW OR A TIME BOMB FOR EACH LAUNCHER: EXPECT AT LEAST TWO. PACK MORE IN THE PODS.', QUIET_PISTOL],
  },
  'fuel-depot': {
    situation: 'A FUEL DEPOT: FOUR TO SIX STORAGE TANKS INSIDE A WIRE PERIMETER.',
    mission: fixed('DESTROY THE FUEL TANKS. SIX IN TEN IS SUCCESS; ALL OF THEM IS THE BONUS.'),
    execution: [
      'A TANK IS SOFT: A GRENADE, A LAW OR A BOMB.',
      'THE TANKS CHAIN. ONE WELL-PLACED CHARGE CAN TAKE SEVERAL: PICK THE ONE IN THE MIDDLE.',
    ],
    special: rules(
      'EVERY TANK GOES UP WITH A VERY LARGE BLAST, AND THE BLASTS CHAIN FASTER THAN YOU CAN RUN. USE THE LONGEST'
        + ' FUSE, OR A LAW FROM RANGE, AND BE WELL CLEAR. THE FIRE DOES NOT CARE WHOSE SIDE YOU ARE ON.',
      NO_NOISE_RULE,
    ),
    equipment: ['ANYTHING THAT EXPLODES. A TIME BOMB ON A LONG FUSE IS THE SAFE WAY TO START THE CHAIN.'],
  },
  'coastal-guns': {
    situation: 'TWO COASTAL GUNS IN CONCRETE CASEMATES, FACING AWAY FROM YOU, TO SEAWARD. AUTOMATED MINI-BUNKERS'
      + ' COVER THEIR REAR.',
    mission: fixed('DESTROY BOTH GUNS.'),
    execution: [
      'A CASEMATE SHRUGS OFF THE LAW AND THE GRENADE. IT OPENS ONLY TO A TIME BOMB SET AT ITS REAR DOOR.',
      'THE REAR DOORS ARE ON THE NORTH SIDE. WORK ROUND BEHIND THE GUNS. A CHARGE AGAINST THE FRONT OR THE SIDES'
        + ' DOES NOTHING BUT TELL THEM YOU ARE THERE.',
    ],
    special: rules(
      'MINI-BUNKERS COVER THE REAR DOORS. THEY CYCLE LIVE AND DORMANT AND FIRE ON ANYTHING MOVING FASTER THAN A'
        + ' CRAWL. TIME THE CYCLE, OR KILL ONE WITH A LAW.',
      NO_NOISE_RULE,
    ),
    equipment: [
      'TWO GUNS, TWO TIME BOMBS. YOU CARRY ONE: PACK THE SECOND, AND A SPARE, IN THE PODS. NO BOMBS, NO MISSION.',
      'A LAW FOR THE MINI-BUNKERS.',
    ],
  },
  'ammo-barges': {
    situation: 'THREE AMMUNITION BARGES MOORED ALONG A CANAL. A TOWPATH RUNS BESIDE THEM.',
    mission: fixed('SINK ALL THREE BARGES.'),
    execution: [
      'EACH BARGE NEEDS A TIME BOMB, PLACED FROM THE TOWPATH AGAINST THE HULL. YOU DO NOT HAVE TO GET WET. GRENADES'
        + ' AND THE LAW ONLY SCAR A LADEN HULL.',
      'A BARGE FULL OF AMMUNITION GOES UP HARD. SET THE FUSE LONG AND BE OFF THE TOWPATH.',
    ],
    special: rules(
      'THE TOWPATH IS PATROLLED, AND LIT AT NIGHT. WORK BETWEEN THE PATROLS.',
      'THERE IS NO KNIFE RULE, BUT A QUIET APPROACH KEEPS THE PATROLS ON THEIR BEAT.',
    ),
    equipment: ['THREE BARGES, THREE TIME BOMBS. YOU CARRY ONE: PACK THE OTHERS IN THE PODS.'],
  },
};
